import { Box } from '@mui/material'
import jschardet from 'jschardet'
import { type FC, type ReactElement, useEffect, useRef, useState } from 'react'

import { useAppSelector } from '../../../../../shared/hooks'
import type {
	TExportContentProps,
	TExportedProfile,
	TExportState
} from '../../types.ts'
import ExportContent from '../ExportContent/ExportContent.tsx'
import ExportStep from '../ExportStep/ExportStep.tsx'

const ExportFromProfileUrls: FC<TExportContentProps> = ({
	contactsCount,
	option,
	setContactsCount,
	setDataToExport
}: TExportContentProps): ReactElement => {
	const { appExportConnector } = useAppSelector(state => state.app)
	const profilesExport = appExportConnector?.profilesExport
	const [urls, setUrls] = useState('')
	const debounceTimeout = useRef<null | ReturnType<typeof setTimeout>>(null)

	const readTextContent = async (
		text: string,
		onRead: (content: string, encoding?: string) => void
	) => {
		if (!text.trim() || !profilesExport) {
			return
		}

		const getExportState = (await profilesExport.getState()) as
			| TExportState
			| null
			| undefined

		if (getExportState) {
			if (
				getExportState.phase === 'completed' ||
				getExportState.phase === 'failed'
			) {
				await profilesExport.clear()
			}
		}

		const encoding = jschardet.detect(text).encoding

		onRead(text, encoding)
	}

	const handleFileRead = async (content: string) => {
		const response = await profilesExport?.extractLinkedInContacts(content)
		const list = Array.isArray(response)
			? (response as TExportedProfile[])
			: null

		setDataToExport(list)
		setContactsCount(list?.length ?? null)
	}

	useEffect(() => {
		if (urls) {
			if (debounceTimeout.current) {
				clearTimeout(debounceTimeout.current)
			}

			debounceTimeout.current = setTimeout(async () => {
				await readTextContent(urls, handleFileRead)
			}, 300)
		} else {
			setDataToExport(null)
			setContactsCount(null)
		}

		return () => {
			if (debounceTimeout.current) {
				clearTimeout(debounceTimeout.current)
			}
		}
	}, [urls])

	const { placeholder = '', title = '' } = option

	return (
		<ExportStep title={title}>
			<ExportContent contactsCount={contactsCount} option={option}>
				<Box
					className='textarea'
					component='textarea'
					onChange={event => setUrls(event.target.value)}
					placeholder={placeholder}
					rows={8}
					sx={{ padding: '10px', resize: 'none' }}
					value={urls}
				/>
			</ExportContent>
		</ExportStep>
	)
}

export default ExportFromProfileUrls
